import { gql } from '@apollo/client';

export const SENSOR_CHANNEL_STATE_RESPONSE_FRAGMENT = gql`
  fragment SensorChannelStateResponse on SensorChannelStateResponse {
    sensorType
    sensorValue: value
    unit
  }
`;

export const BINARY_SENSOR_CHANNEL_STATE_RESPONSE_FRAGMENT = gql`
  fragment BinarySensorChannelStateResponse on BinarySensorChannelStateResponse {
    binarySensorType: sensorType
    binarySensorValue: value
  }
`;

export const CHANNELS_GET_ALL = gql`
  ${SENSOR_CHANNEL_STATE_RESPONSE_FRAGMENT}
  ${BINARY_SENSOR_CHANNEL_STATE_RESPONSE_FRAGMENT}

  query ChannelsGetAll($installationId: UUID!) {
    allUserChannels(installationId: $installationId, order: { deviceId: ASC, alias: ASC }) {
      installationId
      usesCounter
      grantedUses
      alias
      userId
      iconName
      isVisible
      id
      deviceId
      channelType
      currentFirmwareVersion
      favouritePositions {
        ... on PositionFavourites {
          value
        }
        ... on LightsFavourites {
          value
          brightness
          color {
            red
            green
            blue
          }
        }
        ... on SetOnTimeFavourites {
          value
        }
      }
      personalization {
        __typename
        type
        ... on NoPersonalization {
          type
          __typename
        }
        ... on GatePersonalization {
          isQuickControl
          onEventAction
          type
          __typename
        }
      }
      payload {
        id: channelId
        channelId
        deviceConnectionState
        deviceId
        ... on SwitchChannelStateResponse {
          state
          measuredBy {
            meterChannelId
            phase
          }
        }
        ... on BlindChannelStateResponse {
          position
          measuredBy {
            meterChannelId
            phase
          }
          direction
          calibrateState {
            closingMilliseconds
            openingMilliseconds
            progress
            started
            calibrateBlindError: error
          }
        }
        ... on MeterChannelStateResponse {
          meterType
          supportedVectorParameters
          supportedScalarParameters
          effectiveSupportedAggregateVectorParameters
          payload {
            ... on InternalMeasurementPayload {
              id: channelId
              channelId
            }
            ... on DefaultMeterTypeMeasurementPayload {
              id: channelId
              channelId
            }
            ... on StandaloneMeasurementPayload {
              id: channelId
              channelId
            }
          }
        }
        ... on LightChannelStateResponse {
          supportedModes
          toggleValue
          brightness
          temperature
          color {
            r
            g
            b
          }
        }
        ... on OptimizerChannelStateResponse {
          __typename
          mode
          supportedVectorParameters
          supportedScalarParameters
          supportedAggregateVectorParameters
        }
        ... on GateChannelStateResponse {
          gateKind
          position
          partialControlModeStatus
          dailyModeStatus
          lockStatus
          gateDirection: direction
          gateMode
          supportedGateFeatures
          dependantChannels {
            channelId
            channelType
            index
          }
          calibrateState {
            closingMilliseconds
            openingMilliseconds
            progress
            started
            calibrateGateError: error
          }
        }
        ... on GenericInputChannelStateResponse {
          genericInputType
          bistableMode
          monostableMode
        }
        ... on SensorChannelStateResponse {
          ...SensorChannelStateResponse
        }
        ... on BinarySensorChannelStateResponse {
          ...BinarySensorChannelStateResponse
        }
      }
    }
  }
`;

export const CHANNEL_DETAILS = gql`
  ${SENSOR_CHANNEL_STATE_RESPONSE_FRAGMENT}
  ${BINARY_SENSOR_CHANNEL_STATE_RESPONSE_FRAGMENT}

  fragment IntValidationFragment on IntValidation {
    min
    max
    step
  }

  fragment FloatValidationFragment on FloatValidation {
    min
    max
    step
  }

  fragment RelayConfigFragment on RelayConfig {
    enabled
    index
    thresholdValue
    timeOff
    timeOn
  }

  fragment VoltageThresholdFragment on VoltageThreshold {
    index
    value
  }

  fragment WorkingHoursFragment on WorkingHours {
    from
    to
  }

  fragment TariffFragment on Tariff {
    hoursRanges {
      from
      to
      timeZoneKind
    }
    wholeWeekendCheaper
    wholeHolidaysCheaper
    ignoreSummerTimeChanges
    kind
  }

  query ChannelsDetails($installationId: UUID!, $channelId: UUID!) {
    userChannel(installationId: $installationId, id: $channelId) {
      installationId
      usesCounter
      grantedUses
      alias
      userId
      iconName
      isVisible
      id
      deviceId
      channelType
      currentFirmwareVersion
      favouritePositions {
        ... on PositionFavourites {
          value
        }
        ... on LightsFavourites {
          value
          brightness
          color {
            red
            green
            blue
          }
        }
        ... on SetOnTimeFavourites {
          value
        }
      }
      personalization {
        __typename
        type
        ... on NoPersonalization {
          type
          __typename
        }
        ... on GatePersonalization {
          isQuickControl
          isPartialControlModeVisible
          isDailyModeVisible
          onEventAction
          type
          __typename
        }
      }
      payload {
        channelId
        id: channelId
        deviceConnectionState
        deviceId
        ... on SwitchChannelStateResponse {
          state
          supplyReturnBehavior
          measuredBy {
            meterChannelId
            phase
          }
        }
        ... on BlindChannelStateResponse {
          position
          measuredBy {
            meterChannelId
            phase
          }
          supportedBlindFeatures
          sensitivityAOK
          direction
          calibrateState {
            closingMilliseconds
            openingMilliseconds
            progress
            started
            calibrateBlindError: error
          }
        }
        ... on MeterChannelStateResponse {
          meterType
          ampTransformers
          validationRules {
            key
            hysteresis {
              min
              max
              step
            }
            validation {
              min
              max
              step
            }
          }
          meterPurpose
          measuredChannels {
            channelId
            phase
          }
          tariff {
            kind
            wholeWeekendCheaper
            wholeHolidaysCheaper
            ignoreSummerTimeChanges
            hoursRanges {
              from
              to
              timeZoneKind
              pricingTier
            }
          }
          payload {
            ... on InternalMeasurementPayload {
              id: channelId
              channelId
            }
            ... on DefaultMeterTypeMeasurementPayload {
              id: channelId
              channelId
            }
            ... on StandaloneMeasurementPayload {
              id: channelId
              channelId
            }
          }
          supportedVectorParameters
          supportedScalarParameters
          effectiveSupportedAggregateVectorParameters
          eventing {
            supportedRelaysIndices
            supportedSourceThresholds
            supportedControlModes
            rules {
              enabled
              controlMode
              thresholdEvent {
                source
                deadTimeInMs
                hysteresis
                minValue
                maxValue
              }
              priceEvent {
                minPrice
                maxPrice
                startAt
                endAt
                cheapestPeriods
              }
              action {
                index
                isDefaultAction
                isNotifyAction
              }
            }
          }
        }
        ... on LightChannelStateResponse {
          supportedModes
          supplyReturnBehavior
          toggleValue
          brightness
          temperature
          color {
            r
            g
            b
          }
        }
        ... on OptimizerChannelStateResponse {
          __typename
          mode
          supportedVectorParameters
          supportedScalarParameters
          supportedAggregateVectorParameters

          selfConsumptionWithPowerLimit {
            tempMin
            heaterPowerLimit
            tempMax
            tempMinEnabled
            tempSelfConsumption
            powerLimit
            workingHours {
              ...WorkingHoursFragment
            }
            voltageThreshold {
              ...VoltageThresholdFragment
            }
            relaysConfig {
              ...RelayConfigFragment
            }
            tariff {
              ...TariffFragment
            }
          }

          selfConsumptionWithRelays {
            workingHours {
              ...WorkingHoursFragment
            }
            relaysConfig {
              ...RelayConfigFragment
            }
          }

          fullSelfConsumption {
            tempMin
            heaterPowerLimit
            tempMax
            tempMinEnabled
            tempSelfConsumption
            workingHours {
              ...WorkingHoursFragment
            }
            voltageThreshold {
              ...VoltageThresholdFragment
            }
            relaysConfig {
              ...RelayConfigFragment
            }
            tariff {
              ...TariffFragment
            }
          }
          comfort {
            tempMax
            heaterPowerLimit
            tempMin
            voltageThreshold {
              ...VoltageThresholdFragment
            }
            relaysConfig {
              ...RelayConfigFragment
            }
            tariff {
              ...TariffFragment
            }
          }
          eco {
            tempMax
            voltageThreshold {
              ...VoltageThresholdFragment
            }
            relaysConfig {
              ...RelayConfigFragment
            }
          }
          configValidation {
            relay {
              thresholdPercent {
                ...FloatValidationFragment
              }
              thresholdPower {
                ...FloatValidationFragment
              }
              timeOn {
                ...IntValidationFragment
              }
              timeOff {
                ...IntValidationFragment
              }
              __typename
            }
            voltageThreshold {
              ...FloatValidationFragment
            }
            tempMax {
              ...FloatValidationFragment
            }
            tempMin {
              ...FloatValidationFragment
            }
            tempSelfConsumptionMin {
              ...FloatValidationFragment
            }
            heaterPowerLimit {
              ...FloatValidationFragment
            }
            powerLimit {
              ...FloatValidationFragment
            }
            __typename
          }
        }
        ... on GateChannelStateResponse {
          gateKind
          position
          partialControlModeStatus
          dailyModeStatus
          lockStatus
          isNegativeOutput
          alertConfig {
            barrierAlertEnabled
            misdirectionAlertEnabled
            externalSourceTriggeredAlertEnabled
            openedIntervalToNotifyInMs
          }
          gateDirection: direction
          channelId
          gateMode
          sbsSignalTimeInMs
          partialSbsTimeInMs
          tiltTimeInMs
          supportedGateFeatures
          dependantChannels {
            channelId
            channelType
            index
          }
          calibrateState {
            closingMilliseconds
            openingMilliseconds
            progress
            started
            calibrateGateError: error
          }
        }
        ... on GenericInputChannelStateResponse {
          genericInputType
          bistableMode
          monostableMode
        }
        ... on SensorChannelStateResponse {
          ...SensorChannelStateResponse
        }
        ... on BinarySensorChannelStateResponse {
          ...BinarySensorChannelStateResponse
        }
      }
      sharedChannelSettings {
        id
        ... on MeterSharedChannelSettings {
          tariffConfigurations {
            tariff
            prices {
              pricingTier
              price
            }
          }
        }
      }
    }
  }
`;

export const GATE_HISTORY = gql`
  query GateHistory($installationId: UUID!, $channelId: UUID!, $count: NonNegativeInt!, $offset: NonNegativeInt!) {
    gateUserChannel: userChannel(installationId: $installationId, id: $channelId) {
      installationId
      id
      deviceId
      channelType
      payload {
        id: channelId
        ... on GateChannelStateResponse {
          ... @defer {
            history(count: $count, offset: $offset) {
              error {
                errorCode
                description
              }
              data {
                totalCount
                entries {
                  id
                  eventType
                  value
                  occuredAt
                  controlledBy {
                    sourceId
                    id
                    controlSource
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
